import { useInView } from 'react-intersection-observer';
import { DealRating } from '~/components/product';
import { BuyNow } from './BuyNow';
import { Popover } from '~/components/ui';
import Image from 'next/image';
import select from '../../../public/appsumo-select.svg';
import originals from '../../../public/originals.svg';
import question from '../../../public/question-mark.svg';
import { useMemo } from 'react';
import sumodayBadge from '~/public/sumoday-badge.svg';
import clsx from 'clsx';
import { BannerDetails, Deal } from '~/types/deal';
import { MobileGallery } from './MobileGallery';
import useUser from '~/lib/user';
import { getUtmParamsForSocialShare } from '~/lib/util';
import ShareModal, { UTMParamsConfig } from '~/components/global/ShareModal';

type ListingType = 'select' | 'original' | 'plus_exclusive';

function ListingTypeLogo({ type }: { type: ListingType }) {
  return (
    <div className="mb-4 flex gap-x-2">
      {type === 'original' && (
        <Image src={originals} height="15" alt="AppSumo Originals" />
      )}
      {type === 'plus_exclusive' && (
        <>
          <Image src={select} height="15" alt="AppSumo Select" />
          <Popover
            content={`Exclusive offers on the hottest software, expertly tested, vetted, and brought to you by the AppSumo team.`}
          >
            <div>
              <Image
                src={question}
                height="16"
                alt=""
                className="cursor-pointer"
              />
            </div>
          </Popover>
        </>
      )}
      {type === 'select' && (
        <>
          <Image src={select} height="15" alt="AppSumo Select" />
          <Popover
            content={`Exclusive offers on the hottest software, expertly tested, vetted, and brought to you by the AppSumo team.`}
          >
            <div>
              <Image
                src={question}
                height="16"
                alt=""
                className="cursor-pointer"
              />
            </div>
          </Popover>
        </>
      )}
    </div>
  );
}

const RefundGuarantee = ({
  calloutType,
  refundableDays,
  isFree,
}: {
  calloutType: string;
  refundableDays: number;
  isFree: boolean;
}) => {
  const isMoneyBackGuarantee = useMemo(
    () => calloutType === 'money-back',
    [calloutType],
  );
  const hasFreeIntervalPlan = useMemo(
    () => calloutType.includes('-free'),
    [calloutType],
  );
  const isRedeemBy = useMemo(() => calloutType === 'redeem-by', [calloutType]);

  const intervalText = useMemo(() => {
    switch (calloutType) {
      case 'annual-free':
        return 'Get 1 year of access.';
      case 'month-free':
        return 'Get 1 month of access.';
      default:
        return 'Get access now.';
    }
  }, [calloutType]);

  const refundTime = useMemo(() => {
    if (refundableDays === 60) {
      return '2 months';
    } else if (refundableDays === 30) {
      return '1 month';
    } else {
      return `${refundableDays} day${refundableDays === 1 ? '' : 's'}`;
    }
  }, [refundableDays]);

  return (
    <div className="bg-gray-200 px-4 py-6 max-sm:mt-4">
      {isMoneyBackGuarantee && (
        <p>
          <strong className="font-bold">
            {refundableDays} day money-back guarantee.
          </strong>{' '}
          Try it out for {refundTime} to make sure it&apos;s right for you!
        </p>
      )}
      {hasFreeIntervalPlan && (
        <p>
          <strong>{intervalText}</strong> Simply redeem the product within 60
          days.
        </p>
      )}
      {isRedeemBy && (
        <p>
          <strong>Get lifetime access.</strong> Just download and save within 60
          days
          {isFree !== true && ' of purchase'}.
        </p>
      )}
    </div>
  );
};

function PricingBanner({ banner }: { banner: BannerDetails }) {
  const SVGIcon = useMemo(() => {
    if (banner?.svg === 'sumo_day') {
      return sumodayBadge;
    }

    return null;
  }, [banner.svg]);

  if (!banner?.svg) {
    return null;
  }

  return (
    <div className="hidden md:flex">
      <div
        className={
          // eslint-disable-next-line tailwindcss/no-custom-classname
          clsx(
            'ml-auto mr-6 rounded-b-md px-2 pb-2 pt-1',
            `bg-${banner.bg_color}`,
          )
        }
      >
        <Image src={SVGIcon} alt={banner.text} />
      </div>
    </div>
  );
}

export function PricingCard({ deal }: { deal: Deal }) {
  const product = deal?.products[0];
  const { story } = product;
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const cardBorderColor = useMemo(() => {
    if (deal.banner_details?.bg_color) {
      return `md:border-t-${deal.banner_details?.bg_color}`;
    }

    return 'md:border-t-transparent';
  }, [deal.banner_details?.bg_color]);

  const { user } = useUser();
  const socialParams: UTMParamsConfig = useMemo(() => {
    const { slug, id } = deal;
    return getUtmParamsForSocialShare({ data: { slug, id }, userId: user?.id });
  }, [deal, user]);

  const shareableLink =
    typeof window === 'undefined'
      ? ''
      : `${window.location.origin}${deal.get_absolute_url}`;

  return (
    <>
      <ShareModal
        modalTitle={deal.public_name}
        subheader={story.subheader}
        href={shareableLink}
        params={socialParams}
        className="mb-3 max-sm:hidden"
      />
      <div
        ref={ref}
        className={clsx(
          'sm:rounded sm:border sm:border-gray-200 sm:shadow md:border-t-4',
          cardBorderColor,
        )}
      >
        <PricingBanner banner={deal.banner_details} />
        <div className="sm:p-7">
          <div className="max-sm:hidden">
            <ListingTypeLogo type={deal.listing_type as ListingType} />
            <h4 className="mb-2 font-header text-2xl font-bold">
              {deal.public_name}
            </h4>
            {!deal.is_info && (
              <>
                <div className="mb-3 font-header text-sm font-semibold">
                  Lifetime deal
                </div>
                <p className="mb-4">{story.card_description}</p>
              </>
            )}
            {!(deal.is_preview || deal.is_info) && (
              <DealRating
                averageRating={deal.deal_review?.average_rating}
                reviewCount={deal.deal_review?.review_count}
                className="mb-3"
                href={`${deal.get_absolute_url}#reviews`}
              />
            )}
          </div>
          <div className="mt-2 md:hidden">
            <MobileGallery deal={deal} socialParams={socialParams} />
          </div>
          <BuyNow deal={deal} inView={inView} />
        </div>
        {deal.money_back_callout_type && (
          <RefundGuarantee
            calloutType={deal.money_back_callout_type}
            refundableDays={deal.refundable_days}
            isFree={deal.is_free}
          />
        )}
      </div>
    </>
  );
}
